import { CARD_SECTIONS, type StreamDto } from '../../lib/types';
import { sectionCards } from '../../lib/boardMove';
import { SectionDropZone } from './SectionDropZone';
import type { SetMonth } from './CardTile';

interface StreamColumnProps {
  stream: StreamDto;
  canWrite: boolean;
  onOpenCard: (cardId: number) => void;
  onEditStream: (streamId: number) => void;
  /** Write users: nudge the stream's dev headcount by ±1 straight from the header. */
  onAdjustWorkers: (streamId: number, delta: 1 | -1) => void;
  onSetMonth?: SetMonth;
}

const stepBtn =
  'w-6 h-6 rounded-full border border-white/40 text-white text-sm leading-none flex items-center justify-center ' +
  'hover:bg-white/15 focus:outline-none focus-visible:ring-2 ring-white disabled:opacity-30 disabled:hover:bg-transparent';

export function StreamColumn({ stream, canWrite, onOpenCard, onEditStream, onAdjustWorkers, onSetMonth }: StreamColumnProps) {
  return (
    <section
      // min-width = one seventh of the board (6 gaps of 0.75rem removed), so an 8th stream overflows into a scroll.
      className="flex-1 min-w-[calc((100%_-_4.5rem)/7)] bg-slate-50 border border-slate-200 rounded-lg overflow-hidden"
      aria-label={stream.name}
    >
      {/* Name on its own line (the controls row below would otherwise squeeze it to a letter at 7 columns). */}
      <header className="bg-atg-navy rounded-t-lg px-3 py-2 flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-bold text-white truncate min-w-0" title={stream.name}>{stream.name}</h2>
          {canWrite && (
            <button
              type="button"
              onClick={() => onEditStream(stream.id)}
              aria-label={`Edit stream ${stream.name}`}
              className="ml-auto p-1 rounded text-white/80 hover:text-white hover:bg-white/10 focus:outline-none focus-visible:ring-2 ring-white"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4" aria-hidden="true">
                <path d="M13.586 3.586a2 2 0 1 1 2.828 2.828l-.793.793-2.828-2.828.793-.793ZM11.379 5.793 3 14.172V17h2.828l8.38-8.379-2.83-2.828Z" />
              </svg>
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canWrite && (
            <button
              type="button"
              onClick={() => onAdjustWorkers(stream.id, -1)}
              disabled={stream.workerCount <= 0}
              aria-label={`Remove a dev from ${stream.name}`}
              className={stepBtn}
            >
              −
            </button>
          )}
          <span
            className={`text-xs font-semibold rounded-full px-2 py-0.5 whitespace-nowrap ${
              stream.usedWorkers > stream.workerCount ? 'bg-red-600 text-white' : 'bg-atg-accent text-atg-navy'
            }`}
            aria-live="polite"
          >
            {stream.usedWorkers} of {stream.workerCount} used
          </span>
          {canWrite && (
            <button
              type="button"
              onClick={() => onAdjustWorkers(stream.id, 1)}
              aria-label={`Add a dev to ${stream.name}`}
              className={stepBtn}
            >
              +
            </button>
          )}
        </div>
      </header>
      <div className="p-2 space-y-2">
        {CARD_SECTIONS.map((section) => (
          <SectionDropZone
            key={section}
            streamId={stream.id}
            section={section}
            cards={sectionCards(stream.cards, stream.id, section)}
            canWrite={canWrite}
            onOpenCard={onOpenCard}
            onSetMonth={onSetMonth}
          />
        ))}
      </div>
    </section>
  );
}
